import React, { useState, useEffect, useContext } from 'react';
import { TrendingUp, TrendingDown, DollarSign, PieChart, Calendar, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import api from '../api/axiosInstance';
import { AuthContext } from '../App';

const MonthlyPL = () => {
    const { user } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
    });

    useEffect(() => {
        fetchPL();
    }, [date]);

    const fetchPL = async () => {
        setLoading(true);
        try {
            const res = await api.get(`get_monthly_pl.php?month=${date.month}&year=${date.year}`);
            setData(res.data);
        } catch (err) {
            console.error("P&L Fetch Failed", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return (
        <div className="h-96 flex flex-col items-center justify-center text-slate-400 font-black italic uppercase tracking-widest">
            <Loader2 className="animate-spin mb-4 text-indigo-600" size={48} />
            Calculating Profit & Loss...
        </div>
    );

    if (!data || data.status === 'error') return (
        <div className="p-20 text-center text-slate-400 font-bold flex flex-col items-center gap-4">
            <AlertCircle size={48} className="text-rose-500" />
            <p>No financial data found for this period.</p>
        </div>
    );

    const { summary, breakdown } = data;

    return (
        <div className="space-y-10 animate-in fade-in duration-500">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic">Profit & Loss</h1>
                    <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">{`${user.company_name} • Monthly Statement`}</p>
                </div>
                <div className="flex gap-2 bg-white border border-slate-100 p-3 rounded-[24px] shadow-sm">
                    <select
                        className="bg-transparent font-black text-xs uppercase outline-none px-4 border-r border-slate-100"
                        value={date.month}
                        onChange={e => setDate({ ...date, month: e.target.value })}
                    >
                        {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                            <option key={i + 1} value={i + 1}>{m}</option>
                        ))}
                    </select>
                    <input
                        type="number"
                        value={date.year}
                        className="bg-transparent w-20 text-center font-black text-xs outline-none"
                        onChange={e => setDate({ ...date, year: e.target.value })}
                    />
                </div>
            </div>

            {/* FINANCIAL CARDS */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm group hover:scale-105 transition-transform">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Income (Cleared)</p>
                    <h2 className="text-3xl font-black text-emerald-600 italic">₹{summary.income.toLocaleString()}</h2>
                </div>
                <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm group hover:scale-105 transition-transform">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Vendor Bills Paid</p>
                    <h2 className="text-3xl font-black text-slate-900 italic">₹{summary.vendor_expense.toLocaleString()}</h2>
                </div>
                <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm group hover:scale-105 transition-transform">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Site Petty Cash</p>
                    <h2 className="text-3xl font-black text-slate-900 italic">₹{summary.site_petty_expense.toLocaleString()}</h2>
                </div>
                <div className={`p-8 rounded-[40px] shadow-2xl relative overflow-hidden group hover:scale-105 transition-transform ${summary.net_profit >= 0 ? 'bg-indigo-600 text-white' : 'bg-rose-500 text-white'}`}>
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">Monthly Net</p>
                        <h2 className="text-4xl font-black italic tracking-tighter">₹{summary.net_profit.toLocaleString()}</h2>
                    </div>
                    {summary.net_profit >= 0 ? <TrendingUp size={100} className="absolute -right-8 -bottom-8 opacity-10 rotate-12" /> : <TrendingDown size={100} className="absolute -right-8 -bottom-8 opacity-10 -rotate-12" />}
                </div>
            </div>

            {/* SITE BREAKDOWN */}
            <div className="bg-white rounded-[48px] border border-slate-100 p-12 shadow-sm">
                <div className="flex justify-between items-center mb-10">
                    <h3 className="text-2xl font-black italic uppercase tracking-tighter flex items-center gap-3">
                        <PieChart size={24} className="text-indigo-600" /> Site Performance
                    </h3>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Sites this Month: {breakdown.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {breakdown.map((site, i) => (
                        <div key={i} className="flex items-center justify-between p-8 bg-slate-50 rounded-[32px] border border-slate-100 hover:border-indigo-200 hover:bg-white hover:shadow-xl transition-all group">
                            <div>
                                <h4 className="font-black text-slate-900 uppercase text-lg italic tracking-tight group-hover:text-indigo-600">{site.project_name}</h4>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Project Code: {site.id || 'N/A'}</p>
                            </div>
                            <div className="flex gap-8 text-right">
                                <div>
                                    <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Received</p>
                                    <p className="font-black text-emerald-600">₹{parseFloat(site.site_income || 0).toLocaleString()}</p>
                                </div>
                                <div className="border-l border-slate-200 pl-8">
                                    <p className="text-[9px] font-black text-slate-400 uppercase mb-1">Site Petty</p>
                                    <p className="font-black text-rose-500">₹{parseFloat(site.site_petty || 0).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* --- TAX SUMMARY SECTION --- */}
            <div className="mt-10 bg-slate-50 border border-slate-100 rounded-[40px] p-10">
                <div className="flex justify-between items-start mb-8">
                    <div>
                        <h3 className="text-xl font-black italic uppercase tracking-tighter flex items-center gap-2">
                            <DollarSign size={20} className="text-amber-500" /> GST Ledger Summary
                        </h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Calculated for the current selected period</p>
                    </div>
                    <div className="bg-white px-6 py-2 rounded-full border border-slate-200 text-[10px] font-black uppercase text-slate-500">
                        Internal Audit Use Only
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* GST COLLECTED */}
                    <div className="p-6 bg-white rounded-3xl border border-slate-100 shadow-sm">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Output GST (Collected)</p>
                        <div className="flex items-end gap-2">
                            <span className="text-2xl font-black text-slate-900">₹{summary.tax.collected.toLocaleString()}</span>
                            <span className="text-[10px] font-bold text-emerald-500 mb-1">+ Cash Inflow</span>
                        </div>
                    </div>

                    {/* GST PAID */}
                    <div className="p-6 bg-white rounded-3xl border border-slate-100 shadow-sm">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Input GST (Paid to Vendors)</p>
                        <div className="flex items-end gap-2">
                            <span className="text-2xl font-black text-slate-900">₹{summary.tax.paid.toLocaleString()}</span>
                            <span className="text-[10px] font-bold text-rose-500 mb-1">- Tax Credit</span>
                        </div>
                    </div>

                    {/* NET LIABILITY */}
                    <div className={`p-6 rounded-3xl border flex flex-col justify-center ${summary.tax.liability >= 0 ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
                        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Estimated Net Liability</p>
                        <h2 className={`text-3xl font-black italic ${summary.tax.liability >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                            ₹{Math.abs(summary.tax.liability).toLocaleString()}
                            <span className="text-xs ml-2 uppercase not-italic font-bold">
                                {summary.tax.liability >= 0 ? 'Payable' : 'Refundable'}
                            </span>
                        </h2>
                    </div>
                </div>

                <div className="mt-8 p-4 bg-indigo-50/50 rounded-2xl flex items-center gap-3 border border-indigo-100/50">
                    <div className="bg-indigo-600 text-white p-2 rounded-lg">
                        <ArrowRight size={14} />
                    </div>
                    <p className="text-xs text-indigo-900 font-medium">
                        Pro Tip: Ensure all <b>Vendor Invoices</b> have GST numbers attached to claim the Input Tax Credit of ₹{summary.tax.paid.toLocaleString()}.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MonthlyPL;